import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, RefreshCcw, Calendar, MessageSquare, Loader2, Phone, PawPrint } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { adminApi, setAdminToken } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { CONTACT } from '../data/mock';

const APPT_STATUSES = ['NEW', 'CONTACTED', 'CONFIRMED', 'COMPLETED', 'CANCELLED', 'NO_SHOW'];
const CONTACT_STATUSES = ['New', 'Contacted', 'Booked', 'Closed'];

const statusColor = {
  NEW: 'bg-amber-100 text-amber-800 border-amber-200',
  CONTACTED: 'bg-blue-100 text-blue-800 border-blue-200',
  CONFIRMED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  COMPLETED: 'bg-slate-100 text-slate-600 border-slate-200',
  CANCELLED: 'bg-rose-100 text-rose-700 border-rose-200',
  NO_SHOW: 'bg-orange-100 text-orange-800 border-orange-200',
  New: 'bg-amber-100 text-amber-800 border-amber-200',
  Contacted: 'bg-blue-100 text-blue-800 border-blue-200',
  Booked: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Closed: 'bg-slate-100 text-slate-600 border-slate-200',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const AdminDashboard = () => {
  const nav = useNavigate();
  const { toast } = useToast();
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [apptRes, contactRes, statsRes] = await Promise.all([
        adminApi.get('/admin/appointments'),
        adminApi.get('/admin/contacts'),
        adminApi.get('/admin/stats'),
      ]);
      setAppointments(apptRes.data);
      setContacts(contactRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast({ title: 'Failed to load', description: e?.response?.data?.detail || 'Try again.' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const logout = () => {
    setAdminToken(null);
    nav('/admin/login', { replace: true });
  };

  const updateStatus = async (kind, id, status, internal_notes) => {
    try {
      const body = kind === 'appointments' ? { status, internal_notes: internal_notes || '' } : { status };
      await adminApi.patch(`/admin/${kind}/${id}/status`, body);
      toast({ title: 'Status updated', description: `Marked as ${status}` });
      fetchData();
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || 'Try again.' });
    }
  };

  const filterRows = (rows) => filter === 'all' ? rows : rows.filter((r) => r.status === filter);

  return (
    <div className="min-h-screen bg-[#faf5fb]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={CONTACT.logo} alt="Ichi & Ori" className="h-10 w-10 object-contain" />
            <div>
              <h1 className="font-serif text-[18px] font-bold text-[#1a0b3d] leading-tight">Enquiry Dashboard</h1>
              <p className="text-[12px] text-slate-500">Ichi &amp; Ori Pet Care</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchData} className="gap-2">
              <RefreshCcw size={14} /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={logout} className="gap-2 text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200">
              <LogOut size={14} /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
          <StatCard label="Total Appointments" value={stats?.appointments?.total ?? '—'} icon={Calendar} />
          <StatCard label="New Appointments" value={stats?.appointments?.new ?? '—'} icon={PawPrint} highlight />
          <StatCard label="Total Contacts" value={stats?.contacts?.total ?? '—'} icon={MessageSquare} />
          <StatCard label="New Contacts" value={stats?.contacts?.new ?? '—'} icon={MessageSquare} highlight />
        </div>

        {/* Filter */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <h2 className="font-serif text-[24px] font-bold text-[#1a0b3d]">Enquiries</h2>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 bg-white">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {APPT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              {CONTACT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="appointments">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="appointments" className="data-[state=active]:bg-[#5b3eb5] data-[state=active]:text-white">
              Appointments ({appointments.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-[#5b3eb5] data-[state=active]:text-white">
              Contact Messages ({contacts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="appointments">
            {loading ? <LoadingRow /> : (
              <AppointmentsTable
                rows={filterRows(appointments)}
                onStatusChange={(id, s, note) => updateStatus('appointments', id, s, note)}
              />
            )}
          </TabsContent>

          <TabsContent value="contacts">
            {loading ? <LoadingRow /> : (
              <ContactsTable
                rows={filterRows(contacts)}
                onStatusChange={(id, s) => updateStatus('contacts', id, s)}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, highlight }) => (
  <div className={`rounded-2xl p-5 border ${highlight ? 'bg-gradient-to-br from-[#5b3eb5] to-[#7c4ec9] text-white border-transparent' : 'bg-white border-slate-200'}`}>
    <div className="flex items-center justify-between mb-2">
      <span className={`text-[12px] font-medium uppercase tracking-wider ${highlight ? 'text-white/80' : 'text-slate-500'}`}>{label}</span>
      <Icon size={18} className={highlight ? 'text-white/80' : 'text-[#5b3eb5]'} />
    </div>
    <div className="font-serif font-extrabold text-[32px] leading-none">{value}</div>
  </div>
);

const LoadingRow = () => (
  <div className="bg-white rounded-2xl p-10 mt-3 text-center text-slate-500 border border-slate-200">
    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-[#5b3eb5]" />
    Loading enquiries...
  </div>
);

const EmptyRow = ({ message }) => (
  <div className="bg-white rounded-2xl p-10 mt-3 text-center text-slate-500 border border-slate-200">
    {message}
  </div>
);

const StatusSelect = ({ value, onChange, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`h-8 text-[12px] w-36 border ${statusColor[value] || ''}`}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {options.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
    </SelectContent>
  </Select>
);

const AppointmentsTable = ({ rows, onStatusChange }) => {
  if (!rows.length) return <EmptyRow message="No appointment enquiries yet." />;
  const handleChange = (id, newStatus) => {
    const note = window.prompt(`Add an internal note for this status change to ${newStatus} (optional):`, '') || '';
    onStatusChange(id, newStatus, note);
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 mt-3 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[14px]">
          <thead className="bg-[#faf5fb] text-slate-600 text-[12px] uppercase tracking-wider">
            <tr>
              <Th>Request ID</Th><Th>Date</Th><Th>Name</Th><Th>Phone</Th><Th>Pet</Th><Th>Service</Th><Th>Preferred</Th><Th>Status</Th><Th>Internal Notes</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50 align-top">
                <Td>
                  <span className="font-mono text-[12.5px] font-bold text-[#1a0b3d]" title={r.request_id}>{r.request_id || '—'}</span>
                  {r.scheduling_flag === 'POTENTIAL_TIME_CONFLICT' && (
                    <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[10px] font-semibold">
                      ⚠ Time conflict
                    </div>
                  )}
                </Td>
                <Td>{formatDate(r.created_at)}</Td>
                <Td><span className="font-semibold text-[#1a0b3d]">{r.name}</span></Td>
                <Td><a href={`tel:${r.mobile}`} className="text-[#5b3eb5] hover:text-[#e870b6] inline-flex items-center gap-1"><Phone size={12} />{r.mobile}</a></Td>
                <Td>{r.pet_name} <Badge variant="outline" className="ml-1 text-[10px]">{r.pet_type}</Badge></Td>
                <Td>{r.service}</Td>
                <Td><span className="text-slate-600">{r.preferred_date}<br/><span className="text-[12px]">{r.preferred_time}</span></span></Td>
                <Td><StatusSelect value={r.status} onChange={(s) => handleChange(r.id, s)} options={APPT_STATUSES} /></Td>
                <Td>
                  <span className="text-slate-600 line-clamp-2 max-w-[220px] text-[12.5px]" title={r.internal_notes || ''}>
                    {r.internal_notes || <span className="text-slate-400">—</span>}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ContactsTable = ({ rows, onStatusChange }) => {
  if (!rows.length) return <EmptyRow message="No contact messages yet." />;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 mt-3 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[14px]">
          <thead className="bg-[#faf5fb] text-slate-600 text-[12px] uppercase tracking-wider">
            <tr>
              <Th>Date</Th><Th>Name</Th><Th>Phone</Th><Th>Email</Th><Th>Subject</Th><Th>Message</Th><Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50 align-top">
                <Td>{formatDate(r.created_at)}</Td>
                <Td><span className="font-semibold text-[#1a0b3d]">{r.name}</span></Td>
                <Td><a href={`tel:${r.mobile}`} className="text-[#5b3eb5] hover:text-[#e870b6]">{r.mobile}</a></Td>
                <Td>{r.email || '—'}</Td>
                <Td>{r.subject || '—'}</Td>
                <Td><span className="text-slate-600 line-clamp-3 max-w-[260px]" title={r.message}>{r.message}</span></Td>
                <Td><StatusSelect value={r.status} onChange={(s) => onStatusChange(r.id, s)} options={CONTACT_STATUSES} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Th = ({ children }) => <th className="px-4 py-3 text-left font-semibold">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3">{children}</td>;

export default AdminDashboard;
